import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { VolunteerApplication as VolunteerForm } from '../lib/types';
import { submitVolunteer } from '../lib/api';

const initialForm: VolunteerForm = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  city: '',
  county: '',
  has_vehicle: '',
  has_own_equipment: '',
  experience: '',
  why_joining: '',
  emergency_contact_name: '',
  emergency_contact_phone: '',
  references_text: '',
  consent_waiver: false,
  consent_background_check: false,
  consent_conduct: false,
  honeypot: '',
};

const inputClass =
  'w-full bg-transparent border-b-2 border-forest/20 focus:border-terracotta focus:outline-none py-3 text-forest placeholder-forest/40 transition-colors';
const labelClass = 'block text-xs tracking-[0.2em] uppercase text-forest/70 mb-2';

export function VolunteerApplication() {
  const [form, setForm] = useState<VolunteerForm>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const navigate = useNavigate();

  function update<K extends keyof VolunteerForm>(field: K, val: VolunteerForm[K]) {
    setForm((prev) => ({ ...prev, [field]: val }));
    if (errors[field as string]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as string];
        return next;
      });
    }
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!form.first_name.trim()) e.first_name = 'Required';
    if (!form.last_name.trim()) e.last_name = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email';
    if (!form.phone.trim()) e.phone = 'Required';
    if (!form.county) e.county = 'Required';
    if (!form.emergency_contact_name.trim()) e.emergency_contact_name = 'Required';
    if (!form.emergency_contact_phone.trim()) e.emergency_contact_phone = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit() {
    setSubmitError(null);
    if (!validate()) {
      setSubmitError('Please fix the highlighted fields above.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!form.consent_waiver || !form.consent_conduct || !form.consent_background_check) {
      setSubmitError('Please confirm all three required acknowledgments before submitting.');
      return;
    }
    setSubmitting(true);
    const result = await submitVolunteer(form);
    setSubmitting(false);
    if (!result.ok) {
      setSubmitError(result.error || 'Something went wrong. Please try again.');
      return;
    }
    navigate('/thank-you/volunteer');
  }

  const errClass = (field: string) => (errors[field] ? 'border-terracotta' : '');

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16">
      <div className="text-terracotta text-sm tracking-[0.25em] uppercase mb-3">Volunteer Application</div>
      <h1
        className="text-5xl text-forest mb-4 leading-tight font-display"
        style={{ fontVariationSettings: '"opsz" 144' }}
      >
        Become a <em>Volunteer Mowkin.</em>
      </h1>
      <p className="text-lg text-forest/75 max-w-2xl mb-6 leading-relaxed">
        Volunteer Mowkins help our pro Mowkins keep up with demand. We
        gather one Saturday a month to take care of yards for neighbors who
        need a hand. No business required — just willingness, reliability,
        and respect for the families we serve.
      </p>
      <div className="bg-forest text-cream p-5 max-w-2xl mb-12 text-sm leading-relaxed">
        <div className="text-xs tracking-[0.2em] uppercase text-sand mb-2">How this works</div>
        After you apply, our team will reach out to vet you — a short phone
        call and reference check. Once you're vetted, you can sign up for the
        first Saturday of any month. You'll get a route assignment with 2–4
        other volunteers, transportation guidance, and equipment if you don't
        have your own.
      </div>

      {submitError && (
        <div className="bg-terracotta/10 border-l-4 border-terracotta p-4 text-terracotta text-sm mb-6">
          {submitError}
        </div>
      )}

      <div className="bg-cream border border-forest/10 p-8 lg:p-12 space-y-10">
        <section>
          <h2 className="text-2xl text-forest mb-6 font-display">01 — Contact</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>First name *</label>
              <input
                className={`${inputClass} ${errClass('first_name')}`}
                value={form.first_name}
                onChange={(e) => update('first_name', e.target.value)}
                placeholder="First"
              />
              {errors.first_name && <div className="text-terracotta text-xs mt-1">{errors.first_name}</div>}
            </div>
            <div>
              <label className={labelClass}>Last name *</label>
              <input
                className={`${inputClass} ${errClass('last_name')}`}
                value={form.last_name}
                onChange={(e) => update('last_name', e.target.value)}
                placeholder="Last"
              />
              {errors.last_name && <div className="text-terracotta text-xs mt-1">{errors.last_name}</div>}
            </div>
            <div>
              <label className={labelClass}>Email *</label>
              <input
                className={`${inputClass} ${errClass('email')}`}
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                placeholder="you@example.com"
                inputMode="email"
              />
              {errors.email && <div className="text-terracotta text-xs mt-1">{errors.email}</div>}
            </div>
            <div>
              <label className={labelClass}>Phone *</label>
              <input
                className={`${inputClass} ${errClass('phone')}`}
                value={form.phone}
                onChange={(e) => update('phone', e.target.value)}
                placeholder="(361) 555-0123"
                inputMode="tel"
              />
              {errors.phone && <div className="text-terracotta text-xs mt-1">{errors.phone}</div>}
            </div>
            <div>
              <label className={labelClass}>City</label>
              <input
                className={inputClass}
                value={form.city}
                onChange={(e) => update('city', e.target.value)}
                placeholder="Corpus Christi"
              />
            </div>
            <div>
              <label className={labelClass}>County you can drive to *</label>
              <select
                className={`${inputClass} ${errClass('county')}`}
                value={form.county}
                onChange={(e) => update('county', e.target.value as VolunteerForm['county'])}
              >
                <option value="">Select county</option>
                <option value="nueces">Nueces</option>
                <option value="san-patricio">San Patricio</option>
                <option value="aransas">Aransas</option>
              </select>
              {errors.county && <div className="text-terracotta text-xs mt-1">{errors.county}</div>}
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl text-forest mb-6 font-display">02 — Capabilities</h2>
          <div className="space-y-6">
            <div>
              <label className={labelClass}>Do you have a vehicle that can carry equipment?</label>
              <div className="grid grid-cols-2 gap-3">
                {(
                  [
                    ['yes', 'Yes — truck, SUV, or trailer'],
                    ['no', 'No — I can ride with someone'],
                  ] as const
                ).map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => update('has_vehicle', val)}
                    className={`py-3 border-2 text-sm transition-colors ${
                      form.has_vehicle === val
                        ? 'border-terracotta bg-terracotta text-cream'
                        : 'border-forest/20 text-forest hover:border-terracotta'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className={labelClass}>Do you have your own equipment?</label>
              <div className="grid grid-cols-3 gap-3">
                {(
                  [
                    ['yes', 'Yes — mower + trimmer'],
                    ['partial', 'Partial — some tools'],
                    ['no', 'No — shared gear is fine'],
                  ] as const
                ).map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => update('has_own_equipment', val)}
                    className={`py-3 px-4 border-2 text-sm transition-colors text-left ${
                      form.has_own_equipment === val
                        ? 'border-terracotta bg-terracotta text-cream'
                        : 'border-forest/20 text-forest hover:border-terracotta'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className={labelClass}>Yard work experience</label>
              <div className="grid grid-cols-3 gap-3">
                {(
                  [
                    ['none', 'New to this'],
                    ['casual', 'I mow my own yard'],
                    ['experienced', 'Years of experience'],
                  ] as const
                ).map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => update('experience', val)}
                    className={`py-3 px-4 border-2 text-sm transition-colors text-left ${
                      form.experience === val
                        ? 'border-terracotta bg-terracotta text-cream'
                        : 'border-forest/20 text-forest hover:border-terracotta'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl text-forest mb-6 font-display">03 — Why you're joining</h2>
          <textarea
            className={inputClass + ' resize-none'}
            rows={4}
            value={form.why_joining}
            onChange={(e) => update('why_joining', e.target.value)}
            placeholder="A few sentences about why this matters to you."
          />
        </section>

        <section>
          <h2 className="text-2xl text-forest mb-6 font-display">04 — Emergency Contact & References</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>Emergency contact name *</label>
              <input
                className={`${inputClass} ${errClass('emergency_contact_name')}`}
                value={form.emergency_contact_name}
                onChange={(e) => update('emergency_contact_name', e.target.value)}
                placeholder="Spouse, parent, friend"
              />
              {errors.emergency_contact_name && (
                <div className="text-terracotta text-xs mt-1">{errors.emergency_contact_name}</div>
              )}
            </div>
            <div>
              <label className={labelClass}>Emergency contact phone *</label>
              <input
                className={`${inputClass} ${errClass('emergency_contact_phone')}`}
                value={form.emergency_contact_phone}
                onChange={(e) => update('emergency_contact_phone', e.target.value)}
                placeholder="(361) 555-0123"
                inputMode="tel"
              />
              {errors.emergency_contact_phone && (
                <div className="text-terracotta text-xs mt-1">{errors.emergency_contact_phone}</div>
              )}
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>References (optional but appreciated)</label>
              <p className="text-xs text-forest/50 mb-2">
                Two people who can vouch for your character. Name and phone is plenty.
              </p>
              <textarea
                className={inputClass + ' resize-none'}
                rows={3}
                value={form.references_text}
                onChange={(e) => update('references_text', e.target.value)}
                placeholder="Jane Smith — (361) 555-0123, neighbor. Pastor John — (361) 555-0145."
              />
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl text-forest mb-6 font-display">05 — Acknowledgments</h2>
          <div className="space-y-4">
            {(
              [
                [
                  'consent_waiver',
                  'I understand volunteering involves physical work with power equipment. I accept personal responsibility for safety, will follow team rules, and release MowKind from liability for ordinary risks of yard work.',
                ],
                [
                  'consent_conduct',
                  'I agree to treat every resident with respect and dignity, keep their information confidential, and represent MowKind professionally on every mow day.',
                ],
                [
                  'consent_background_check',
                  'I authorize MowKind to verify the information in this application, including contacting my references.',
                ],
              ] as const
            ).map(([field, label]) => (
              <label key={field} className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form[field]}
                  onChange={(e) => update(field, e.target.checked)}
                  className="mt-1 accent-terracotta"
                />
                <span className="text-sm text-forest">
                  {label} <span className="text-terracotta">*</span>
                </span>
              </label>
            ))}
          </div>
        </section>

        {/* Honeypot */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={form.honeypot || ''}
          onChange={(e) => update('honeypot', e.target.value)}
          style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
          aria-hidden="true"
        />

        <div className="pt-8 border-t border-forest/10 flex justify-end">
          <button
            onClick={submit}
            disabled={submitting}
            className="bg-terracotta text-cream px-10 py-4 text-sm tracking-[0.1em] uppercase hover:bg-forest transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Submitting…' : 'Submit Application →'}
          </button>
        </div>
      </div>
    </div>
  );
}
